'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { AlertCircle, RotateCcw, Search } from 'lucide-react'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log the error for debugging
    console.error('[Home Error]', error)
  }, [error])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Oops! Something went wrong
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          We encountered an error while loading your request.
        </p>
      </div>

      <div className="flex items-start gap-4 rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-900 dark:bg-amber-950">
        <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2">
            {error?.message || 'An unexpected error occurred'}
          </h2>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
            This might be a temporary issue. Please try refreshing the page or going back to the home page.
          </p>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => reset()}
              className="flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors dark:bg-amber-700 dark:hover:bg-amber-600"
            >
              <RotateCcw className="h-4 w-4" />
              Try Again
            </button>
            <Link
              href="/"
              className="flex items-center gap-2 rounded-md border border-amber-300 px-4 py-2 text-sm font-medium text-amber-600 hover:bg-amber-50 transition-colors dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
            >
              <Search className="h-4 w-4" />
              Browse Galleries
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/50">
        <p className="text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
          Error ID: {error?.digest || 'Unknown'}
        </p>
      </div>
    </div>
  )
}
